import { ICanvasPack } from "./ICanvasPack";
import DefaultCanvasPack from "./DefaultCanvasPack";
import LevelBasedCanvasPack from "./LevelBasedCanvasPack";
import IcyCanvasPack from "./IcyCanvasPack";
import CosmicCanvasPack from "./CosmicCanvasPack";
import ForestCanvasPack from "./ForestCanvasPack";
import LavaCanvasPack from "./LavaCanvasPack";
import NeonGridCanvasPack from "./NeonGridCanvasPack";
import DeepSeaCanvasPack from "./DeepSeaCanvasPack";
import RetroArcadeCanvasPack from "./RetroArcadeCanvasPack";
import ZenGardenCanvasPack from "./ZenGardenCanvasPack";
import AlienRuinCanvasPack from "./AlienRuinCanvasPack";
import MadScienceCanvasPack from "./MadScienceCanvasPack";
import PyrotechnicCanvasPack from "./PyrotechnicCanvasPack";

export type CanvasPackType =
  | "default"
  | "levelBased"
  | "icy"
  | "cosmic"
  | "forest"
  | "lava"
  | "neon"
  | "deepSea"
  | "retro"
  | "zenGarden"
  | "alienRuin"
  | "madScience"
  | "pyrotechnic"
  | "consoleChronicles";

const packCache: Map<CanvasPackType, ICanvasPack> = new Map();

export function getCanvasPack(
  packType: CanvasPackType = "default"
): ICanvasPack {
  // Return cached pack if available
  if (packCache.has(packType)) {
    return packCache.get(packType)!;
  }

  // Create and cache pack
  let pack: ICanvasPack;
  switch (packType) {
    case "levelBased":
      pack = new LevelBasedCanvasPack();
      break;
    case "icy":
      pack = new IcyCanvasPack();
      break;
    case "cosmic":
      pack = new CosmicCanvasPack();
      break;
    case "forest":
      pack = new ForestCanvasPack();
      break;
    case "lava":
      pack = new LavaCanvasPack();
      break;
    case "neon":
      pack = new NeonGridCanvasPack();
      break;
    case "deepSea":
      pack = new DeepSeaCanvasPack();
      break;
    case "retro":
      pack = new RetroArcadeCanvasPack();
      break;
    case "zenGarden":
      pack = new ZenGardenCanvasPack();
      break;
    case "alienRuin":
      pack = new AlienRuinCanvasPack();
      break;
    case "madScience":
      pack = new MadScienceCanvasPack();
      break;
    case "pyrotechnic":
      pack = new PyrotechnicCanvasPack();
      break;
    case "default":
    default:
      pack = new DefaultCanvasPack();
      break;
  }

  packCache.set(packType, pack);
  return pack;
}

export function getCanvasPackFromStorage(): ICanvasPack {
  try {
    const stored = localStorage.getItem("canvasPack");
    const packType = stored ? JSON.parse(stored) : "default";
    return getCanvasPack(packType as CanvasPackType);
  } catch {
    return getCanvasPack("default");
  }
}

/**
 * Get canvas pack for a canvas, using game's canvasPack if this is a remote player,
 * otherwise falling back to localStorage (for local players)
 *
 * For remote players: uses their own canvas pack preference (from playerState.canvasPack) or "default"
 * For local players: uses localStorage preference
 */
export function getCanvasPackForCanvas(canvas?: {
  game?: { isRemote?: boolean; canvasPack?: string };
}): ICanvasPack {
  // Explicitly check if this is a remote player's canvas
  if (canvas?.game?.isRemote) {
    // Remote player: use their own canvas pack preference, or "default" if not set
    // Never use localStorage for remote players - always use their preference or default
    return getCanvasPack(
      (canvas.game.canvasPack || "default") as CanvasPackType
    );
  }
  // Local player: use localStorage
  return getCanvasPackFromStorage();
}
